/*
 * myPokemonDerived.ts
 *
 * Everything a My Pokémon record IMPLIES but the screen never prints:
 *
 *   - LEVEL — from CP + max HP + the three IVs via the GO CP formula
 *     (`ivSolver.computeCp/computeHp`), the same derivation Poké Genie's
 *     overlay shows as "Level 49.0".
 *   - IV percent — the triple as a 0-100.
 *   - PvP league percents — the stat-product percent-of-best under the Great
 *     and Ultra caps (`pvpRank`), Poké Genie's "G: 50.7%".
 *
 * Base stats come from the generated full-coverage table
 * (`speciesBaseStats.generated.ts`, all 1025 species, plus every alternate
 * form whose GO stats differ and the gendered species); a species the table
 * somehow misses derives nothing rather than guessing. baseStatsForRecord is
 * THE resolver: form id first, then the female row, then the species row.
 *
 * League ranking walks all 4096 IV combos, which is milliseconds once but
 * real money across a list re-render — results are memoized per
 * (species, iv, cp, hp) with a small LRU-ish cap.
 */

import {
  baseStatsForFemale,
  baseStatsForForm,
  baseStatsForSpecies,
  FEMALE_BASE_STATS,
  FORM_BASE_STATS,
} from "./generated/speciesBaseStats.generated";
import { FORM_GROUPS_BY_SPECIES_ID } from "./generated/pokemonForms.generated";
import { computeCp, computeHp } from "./ivSolver";
import { leagueFullRank, leaguePercent, leagueTopRank } from "./pvpRank";

export interface MyPokemonDerived {
  /** Pokémon level (1-51, half steps), or null when underdetermined. */
  level: number | null;
  /** IV total as 0-100 (one decimal), or null while any IV is unread. */
  ivPercent: number | null;
  /** Great League stat-product percent-of-best (one decimal), or null. */
  greatPct: number | null;
  /** Ultra League stat-product percent-of-best (one decimal), or null. */
  ultraPct: number | null;
  /** Master League stat-product percent-of-best (no cap, always eligible). */
  masterPct: number | null;
  /**
   * False when this Pokémon's CP is already OVER the league cap — GO has no
   * power-down, so a CP 4367 Ho-Oh can never enter Great League and quoting
   * its GL% is a claim about a Pokémon that cannot exist (2026-08-20 user
   * report). True when under the cap or when CP is unread.
   */
  greatEligible: boolean;
  ultraEligible: boolean;
  /**
   * The best league in which this exact IV combo is the species' #1, #2 or
   * #3 of all 4096 (eligibility respected), or null. Lowest rank wins across
   * leagues; a tie keeps the smallest league checked first (great → ultra →
   * master). Drives the list card's bottom-left league-rank badge
   * (2026-08-25 ask).
   */
  bestLeagueRank: { league: "great" | "ultra" | "master"; rank: number } | null;
}

export interface MyPokemonDerivedInput {
  speciesId: number | null;
  cp: number | null;
  hpMax: number | null;
  ivAtk: number | null;
  ivDef: number | null;
  ivHp: number | null;
  /**
   * The record's form ("dialga-origin", "raichu-alola"). An alternate form
   * has its own GO stats and so its own CP curve (2026-09-22 owner report:
   * an Origin Forme Dialga at CP 4155 / 175 HP was priced on Dialga's stats,
   * so the app named ~4076 and the real CP sat off the ladder). Absent or
   * base form = the species row.
   */
  formId?: string | null;
  /** Female forms of a few species carry their own GO stats (see FEMALE_FORM_BASE_STATS). */
  gender?: "male" | "female" | null;
}

/**
 * GO gives a handful of species different base stats by gender, and the
 * species table holds the male (default) form only. Without this a female
 * Indeedee's level, ranks and CP ladder were computed on male stats, so the
 * CP GO showed was "impossible" and the CP slider skipped over it
 * (2026-09-11 owner report). Generated from the Game Master since
 * 2026-09-22 (FEMALE_BASE_STATS); this shape is kept for its readers.
 */
export const FEMALE_FORM_BASE_STATS: Record<number, { atk: number; def: number; sta: number }> =
  Object.fromEntries(
    Object.entries(FEMALE_BASE_STATS).map(([id, [atk, def, sta]]) => [Number(id), { atk, def, sta }]),
  );

/**
 * Base stats for a record, the ONE resolver every CP / HP / level / rank
 * computation goes through: the form's own row when the form has one, else
 * the female row when GO splits the species by gender, else the species row.
 */
export function baseStatsForRecord(record: {
  speciesId: number | null;
  formId?: string | null;
  gender?: "male" | "female" | null;
}): { atk: number; def: number; sta: number } | null {
  if (record.speciesId == null) return null;
  const form = baseStatsForForm(record.formId);
  if (form) return form;
  if (record.gender === "female") {
    const female = baseStatsForFemale(record.speciesId);
    if (female) return female;
  }
  return baseStatsForSpecies(record.speciesId);
}

/**
 * Every stats row a species can wear: its own, each alternate form's and
 * the female row. For a scan-time gate that knows the species but not yet
 * the form (the frame never prints "Origin Forme"): a CP is plausible when
 * it sits on ANY of these curves, and only then is it kept for the record
 * whose form decides the exact one.
 */
export function baseStatsCandidatesForSpecies(
  speciesId: number,
): Array<{ atk: number; def: number; sta: number }> {
  const out: Array<{ atk: number; def: number; sta: number }> = [];
  const push = (row: { atk: number; def: number; sta: number } | null) => {
    if (row && !out.some((o) => o.atk === row.atk && o.def === row.def && o.sta === row.sta)) out.push(row);
  };
  push(baseStatsForSpecies(speciesId));
  push(baseStatsForFemale(speciesId));
  const group = FORM_GROUPS_BY_SPECIES_ID.get(speciesId);
  for (const form of group?.forms ?? []) {
    if (form.formId in FORM_BASE_STATS) push(baseStatsForForm(form.formId));
  }
  return out;
}

const EMPTY: MyPokemonDerived = {
  level: null,
  ivPercent: null,
  greatPct: null,
  ultraPct: null,
  masterPct: null,
  greatEligible: true,
  ultraEligible: true,
  bestLeagueRank: null,
};

/**
 * The level at which this species with these IVs produces exactly this CP
 * (and, when known, this max HP). Null when no level matches or more than
 * one does — a wrong level is worse than none.
 */
export function deriveLevel(
  base: { atk: number; def: number; sta: number },
  iv: { atk: number; def: number; sta: number },
  cp: number,
  hpMax: number | null,
): number | null {
  let found: number | null = null;
  for (let half = 2; half <= 102; half++) {
    const level = half / 2;
    if (computeCp(base, iv, level) !== cp) continue;
    if (hpMax != null && computeHp(base, iv.sta, level) !== hpMax) continue;
    if (found != null) return null; // ambiguous
    found = level;
  }
  return found;
}

/** What a manual CP edit resolves to — see resolveManualCp. */
export type ManualCpResolution =
  | { ok: true; cp: number; /** New max HP when the curve names it, else undefined (leave stored HP). */ hpMax?: number }
  | { ok: false; reason: string };

/**
 * Judge + resolve a hand-typed CP for a record (2026-08-24 ask: "the trainer
 * powers up the Pokémon and edits the CP here").
 *
 * A power-up changes CP AND max HP together, so an accepted edit must move
 * both or the record goes curve-inconsistent — and the sanitizer
 * (myPokemonSanitize) would then clear the very CP the trainer just typed.
 * When species + IVs are known we therefore re-solve the level from the new
 * CP alone and return the max HP that level implies. Several half-levels can
 * share a CP; a power-up only ever goes UP, so the HIGHEST matching level
 * wins. A CP no level can produce is rejected with the reason the toast
 * shows. When the species or IVs are unknown the curve can't be consulted:
 * the CP is accepted as typed and the stored HP is left alone (the sanitizer
 * only judges fully-known records, so nothing can wipe it).
 */
export function resolveManualCp(
  record: {
    speciesId: number | null;
    ivAtk: number | null;
    ivDef: number | null;
    ivHp: number | null;
  },
  cp: number,
): ManualCpResolution {
  if (!Number.isInteger(cp) || cp < 10 || cp > 9999) {
    return { ok: false, reason: "CP must be a whole number between 10 and 9999." };
  }
  const base = baseStatsForRecord(record);
  if (
    !base ||
    record.ivAtk == null ||
    record.ivDef == null ||
    record.ivHp == null
  ) {
    return { ok: true, cp };
  }
  const iv = { atk: record.ivAtk, def: record.ivDef, sta: record.ivHp };
  let bestLevel: number | null = null;
  for (let half = 2; half <= 102; half++) {
    const level = half / 2;
    if (computeCp(base, iv, level) === cp) bestLevel = level;
  }
  if (bestLevel == null) {
    return {
      ok: false,
      reason: `No level produces CP ${cp} for this Pokémon's IVs — check the number in GO.`,
    };
  }
  return { ok: true, cp, hpMax: computeHp(base, iv.sta, bestLevel) };
}

/**
 * Where a TYPED CP lands on the ladder (2026-09-10 ask: a number box beside
 * the slider). "exact" is a notch; "nearest" is the closest notch to a CP no
 * level produces for these IVs, which the editor saves with a warning so the
 * record stays on the curve the sanitizer checks (an off-curve CP would be
 * cleared on the next pass, which is worse than a nudge with a toast);
 * "invalid" is not a CP at all.
 */
export type TypedCpOnLadder =
  | { kind: "exact"; idx: number }
  | { kind: "nearest"; idx: number; typed: number }
  | { kind: "invalid" };

export function resolveTypedCpOnLadder(
  ladder: readonly CpLadderStep[],
  text: string,
): TypedCpOnLadder {
  const typed = Number(text.trim());
  if (text.trim() === "" || !Number.isInteger(typed) || typed < 10 || typed > 9999) {
    return { kind: "invalid" };
  }
  let best = -1;
  for (let i = 0; i < ladder.length; i++) {
    if (ladder[i].cp === typed) return { kind: "exact", idx: i };
    if (best < 0 || Math.abs(ladder[i].cp - typed) < Math.abs(ladder[best].cp - typed)) best = i;
  }
  if (best < 0) return { kind: "invalid" };
  return { kind: "nearest", idx: best, typed };
}

/** One notch on a species' power-up ladder — a legal (level, CP, HP) triple. */
export interface CpLadderStep {
  level: number;
  cp: number;
  hpMax: number;
}

/**
 * Every CP this Pokémon can legally have, in level order (2026-08-24 ask:
 * the CP edit is a SLIDER over this ladder, so an illegal CP is simply not
 * expressible). Levels 1–51 in half steps; adjacent low levels can share a
 * CP, and the HIGHEST level keeps the notch (a power-up only moves up, and
 * its HP is the one GO would show). Null when species, IVs or base stats
 * are unknown — the caller then falls back to a typed CP through
 * resolveManualCp, which accepts what it cannot judge.
 */
export function manualCpLadder(record: {
  speciesId: number | null;
  ivAtk: number | null;
  ivDef: number | null;
  ivHp: number | null;
  gender?: "male" | "female" | null;
}): CpLadderStep[] | null {
  const base = baseStatsForRecord(record);
  if (!base || record.ivAtk == null || record.ivDef == null || record.ivHp == null) {
    return null;
  }
  const iv = { atk: record.ivAtk, def: record.ivDef, sta: record.ivHp };
  const steps: CpLadderStep[] = [];
  for (let half = 2; half <= 102; half++) {
    const level = half / 2;
    const cp = computeCp(base, iv, level);
    const step = { level, cp, hpMax: computeHp(base, iv.sta, level) };
    if (steps.length > 0 && steps[steps.length - 1].cp === cp) {
      steps[steps.length - 1] = step; // duplicate CP: highest level keeps it
    } else {
      steps.push(step);
    }
  }
  return steps;
}

/**
 * Appraisal stars derived from the IV total (the exact in-game banding).
 * Derived, not pixel-read: the seal's orange medallion ring reads as gold
 * whether or not any star is earned (2026-08-20 device pass).
 */
export function starsFromIv(iv: { atk: number; def: number; hp: number }): number {
  const total = iv.atk + iv.def + iv.hp;
  if (total >= 37) return 3;
  if (total >= 30) return 2;
  if (total >= 23) return 1;
  return 0;
}

const cache = new Map<string, MyPokemonDerived>();
// Must comfortably exceed the store's record cap (10k): with a 3,000-record
// import, a 400-entry cache was cycled EVERY render, so every row re-ran the
// 4,096-combo league ranking twice per paint — the post-import lag
// (2026-08-20 device report). Entries are ~80 bytes; 12k is ~1MB worst case.
const CACHE_CAP = 12_000;

/** Derive level / IV% / league percents for a record-shaped input. Memoized. */
export function deriveMyPokemon(input: MyPokemonDerivedInput): MyPokemonDerived {
  const { speciesId, cp, hpMax, ivAtk, ivDef, ivHp } = input;
  if (speciesId == null) return EMPTY;
  const key = `${speciesId}|${input.formId ?? ""}|${input.gender === "female" ? "f" : ""}|${cp ?? ""}|${hpMax ?? ""}|${ivAtk ?? ""}|${ivDef ?? ""}|${ivHp ?? ""}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const out: MyPokemonDerived = { ...EMPTY };
  out.greatEligible = cp == null || cp <= 1500;
  out.ultraEligible = cp == null || cp <= 2500;
  if (ivAtk != null && ivDef != null && ivHp != null) {
    out.ivPercent = Math.round(((ivAtk + ivDef + ivHp) / 45) * 1000) / 10;
    const base = baseStatsForRecord(input);
    if (base) {
      const iv = { atk: ivAtk, def: ivDef, sta: ivHp };
      if (cp != null) out.level = deriveLevel(base, iv, cp, hpMax);
      // A league percent is only quoted for a league this Pokémon can enter.
      // leaguePercent, not rankOneLeague: the percent-only fast path shares
      // one cached species-wide sweep per league instead of re-running the
      // 4096-combo ranking per record (the 3,000-record GL/UL sort freeze,
      // 2026-08-21 device report).
      if (out.greatEligible) {
        const gl = leaguePercent("great", { base, iv });
        out.greatPct = gl != null ? Math.round(gl * 10) / 10 : null;
      }
      if (out.ultraEligible) {
        const ul = leaguePercent("ultra", { base, iv });
        out.ultraPct = ul != null ? Math.round(ul * 10) / 10 : null;
      }
      // Master has no CP cap, so every Pokemon is eligible; same cached
      // species-wide sweep as the other leagues (PiP hero line, 2026-08-26).
      const ml = leaguePercent("master", { base, iv });
      out.masterPct = ml != null ? Math.round(ml * 10) / 10 : null;
      // Top-3 badge (2026-08-25 ask). leagueTopRank reads the same cached
      // species-wide sweep leaguePercent just paid for, so the marginal cost
      // per record is one best-level solve per league — list-path cheap.
      for (const league of ["great", "ultra", "master"] as const) {
        if (league === "great" && !out.greatEligible) continue;
        if (league === "ultra" && !out.ultraEligible) continue;
        const rank = leagueTopRank(league, { base, iv });
        if (rank != null && (out.bestLeagueRank == null || rank < out.bestLeagueRank.rank)) {
          out.bestLeagueRank = { league, rank };
        }
      }
    }
  }

  if (cache.size >= CACHE_CAP) {
    // Drop the oldest half — crude, allocation-free, and plenty for a list.
    let i = 0;
    for (const k of cache.keys()) {
      cache.delete(k);
      if (++i >= CACHE_CAP / 2) break;
    }
  }
  cache.set(key, out);
  return out;
}

/**
 * Full 1-4096 league ranks for the nickname rank tokens (2026-08-29 ask:
 * "rank not just percentages"). NOT part of deriveMyPokemon: ranks are only
 * needed when the naming template contains a rank token
 * (ivNicknames.templateWantsRanks), so callers pay for them only then.
 * leagueFullRank keeps one sorted species-wide sweep per (species, cap), so
 * even a whole-box naming queue stays list-path cheap. A league this Pokémon
 * cannot enter (CP already over the cap) reports null, matching the percent
 * rules above.
 */
export function nicknameLeagueRanks(
  input: MyPokemonDerivedInput,
): { greatRank: number | null; ultraRank: number | null } {
  const none = { greatRank: null, ultraRank: null };
  const { speciesId, cp, ivAtk, ivDef, ivHp } = input;
  if (speciesId == null || ivAtk == null || ivDef == null || ivHp == null) return none;
  const base = baseStatsForRecord(input);
  if (!base) return none;
  const iv = { atk: ivAtk, def: ivDef, sta: ivHp };
  return {
    greatRank: cp == null || cp <= 1500 ? leagueFullRank("great", { base, iv }) : null,
    ultraRank: cp == null || cp <= 2500 ? leagueFullRank("ultra", { base, iv }) : null,
  };
}

/**
 * A Pokémon's BEST case, not just its IVs (2026-08-30, the Brionne report: a
 * rank-1 Great League spread hidden behind a mediocre raw IV percent). The
 * winner is the best of: IV percent, and each league's percent-of-best where
 * the Pokémon can actually enter that league. Ties go to IV (a hundo reads
 * "100% IV", not "ML 100%"). `league` names the winning league, or null when
 * plain IVs won — surfaces use it to draw the league ball beside the score.
 * Shared by the naming queue's Best-first ordering and the My Pokémon list
 * ring.
 */
export function bestOptionOf(
  derived: MyPokemonDerived,
): { score: number; label: string; league: "great" | "ultra" | "master" | null } | null {
  const cands: Array<{ score: number; label: string; league: "great" | "ultra" | "master" | null }> = [];
  if (derived.ivPercent != null)
    cands.push({ score: derived.ivPercent, label: `${Math.round(derived.ivPercent)}% IV`, league: null });
  if (derived.greatEligible && derived.greatPct != null)
    cands.push({ score: derived.greatPct, label: `GL ${Math.round(derived.greatPct)}%`, league: "great" });
  if (derived.ultraEligible && derived.ultraPct != null)
    cands.push({ score: derived.ultraPct, label: `UL ${Math.round(derived.ultraPct)}%`, league: "ultra" });
  if (derived.masterPct != null)
    cands.push({ score: derived.masterPct, label: `ML ${Math.round(derived.masterPct)}%`, league: "master" });
  if (cands.length === 0) return null;
  return cands.reduce((a, b) => (b.score > a.score ? b : a));
}

/**
 * The top-1% PvP callout (2026-08-21 ask): the best league in which this
 * Pokémon ranks >= 99%, or null. Master's proxy is overall IV percent (ML
 * rank is a pure stat-product ordering). Shared by the list rows' dot-rail
 * league ball and the live-scan PiP badge so both surfaces light up for
 * exactly the same Pokémon.
 */
export function topLeagueBadge(
  derived: MyPokemonDerived,
): { league: "great" | "ultra" | "master"; pct: number } | null {
  const cands: Array<{ league: "great" | "ultra" | "master"; pct: number }> = [];
  if (derived.greatEligible && derived.greatPct != null && derived.greatPct >= 99)
    cands.push({ league: "great", pct: derived.greatPct });
  if (derived.ultraEligible && derived.ultraPct != null && derived.ultraPct >= 99)
    cands.push({ league: "ultra", pct: derived.ultraPct });
  if (derived.ivPercent != null && derived.ivPercent >= 99)
    cands.push({ league: "master", pct: derived.ivPercent });
  cands.sort((a, b) => b.pct - a.pct);
  return cands[0] ?? null;
}

/** Test seam. */
export function __resetMyPokemonDerivedCacheForTest(): void {
  cache.clear();
}
